import {
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Paper,
  Slider,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { videoStreamingApi } from '../../../services/backend/backendApi';

/**
 * @typedef {Object} VideoSetupPropsArgs
 * @property {import('../../../types/DeviceDescriptor').DeviceDescriptor} descriptor
 * @property {string} chamberNumber
 * @property {string|null} streamId
 * @property {(id: string|null) => void} setStreamId
 * @property {(url: string) => void} setMjpegUrl
 * @property {(isStreaming: boolean) => void} setIsStreaming
 */

/** @param {VideoSetupPropsArgs} props */
export function VideoSetupProps({
  descriptor,
  chamberNumber,
  streamId,
  setStreamId,
  setMjpegUrl,
  setIsStreaming,
}) {
  // Every hook has to run on every render, so they all come before the guards
  // below - React tracks hooks by call order, not by name.
  const [chamber, SetChamber] = useState(Number(chamberNumber.split('-').pop()));
  const [Mode, SetMode] = useState('sync');
  const [loading, setLoading] = useState(false);
  const [brightnessValue, SetBrightnessValue] = useState(5000);
  const [triggerAngleValue, SetTriggerAngleValue] = useState(0);

  const handleChangeSetup = async () => {
    if (!streamId) return;
    try {
      setLoading(true);
      const response = await videoStreamingApi.changeSetup(streamId, {
        selected_chamber: chamber,
        brightness: brightnessValue,
        sync_trigger_angle: triggerAngleValue,
      });
      setStreamId(response.stream_id);
      setMjpegUrl(response.mjpeg_url);
      setIsStreaming(true);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!descriptor) {
    return null;
  }

  const brightness_controller = descriptor.cameraStreamControllers.find((controller) =>
    controller.liveBrightnessActor.chambers.includes(chamber),
  );
  const triggerAngle_controller = descriptor.cameraStreamControllers.find((controller) =>
    controller.liveSyncTriggerAngleActor.chambers.includes(chamber),
  );
  const availableChambers = descriptor.cameraStreamControllers.flatMap(
    (controller) => controller.liveBrightnessActor.chambers,
  );
  if (!brightness_controller || !triggerAngle_controller) {
    return null;
  }

  const brightness_min = brightness_controller.liveBrightnessSensor.minDigitalBrightness;
  const brightness_max = brightness_controller.liveBrightnessSensor.maxDigitalBrightness;

  return (
    <Paper
      sx={{
        p: 2,
        gap: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'top',
        width: '100%',
        height: '50%',
      }}
    >
      <ButtonGroup
        variant="text"
        aria-label="text button group"
        sx={{
          width: '100%',
          overflow: 'hidden',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Button value="sync" onClick={() => SetMode('sync')}>
          Sync
        </Button>
        <Button value="async" disabled={true} onClick={() => SetMode('async')}>
          Async
        </Button>
        <Button value="fluo" disabled={true} onClick={() => SetMode('fluo')}>
          Fluo
        </Button>
      </ButtonGroup>
      <ButtonGroup
        variant="text"
        aria-label="text button group"
        sx={{
          width: '100%',
          overflow: 'hidden',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        {availableChambers.map((chamberValue) => (
          <Button
            key={chamberValue}
            value={chamberValue}
            sx={{ backgroundColor: chamberValue === chamber ? 'success.light' : 'transparent' }}
            onClick={() => SetChamber(chamberValue)}
          >
            {chamberValue}
          </Button>
        ))}
      </ButtonGroup>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'top',
          width: '100%',
        }}
      >
        <Typography>Trigger Angle:</Typography>
        <Slider
          value={triggerAngleValue}
          min={0}
          max={360}
          step={1}
          onChange={(event, value) => {
            SetTriggerAngleValue(Number(value));
          }}
        />
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'top',
          width: '100%',
        }}
      >
        <Typography>Brightness:</Typography>
        <Slider
          value={(brightnessValue / (brightness_max - brightness_min)) * 100}
          min={0}
          max={100}
          step={1}
          onChange={(event, value) => {
            SetBrightnessValue(
              (Number(value) * (brightness_max - brightness_min)) / 100 + brightness_min,
            );
          }}
        />
      </Box>
      <Button
        variant="outlined"
        onClick={handleChangeSetup}
        sx={{ width: '100%' }}
        disabled={loading || !streamId}
      >
        {loading ? <CircularProgress size={16} /> : 'Change Setup'}
      </Button>
    </Paper>
  );
}
